/* =====================================================================
 * CollaborationModal — Confluence / Notion 페이지 선택 모달
 * ===================================================================== */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import styled, { css } from 'styled-components';
import {
  Blocks,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  FileIcon,
  FileText,
  Folder,
  FolderOpen,
  Loader,
  Paperclip,
  RefreshCw,
} from 'lucide-react';

import { Modal } from '@auw/workspace-ui';
import { Button } from '@auw/workspace-ui';
import { EmptyState } from '@auw/workspace-ui';
import { Checkbox } from '@auw/workspace-ui';
import { Input } from '@auw/workspace-ui';
import { SkeletonLine } from '@auw/workspace-ui';
import { Tabs, type TabItem } from '@auw/workspace-ui';
import { RingSpinner } from '@auw/workspace-ui';
import {
  getCollaborationAttachmentOpenTarget,
  getConfluencePage,
  getCollaborationPagePdfUrl,
  getNotionPage,
  listConfluencePageAttachments,
  listConfluenceTreeNodes,
  listConfluencePages,
  listNotionPageAttachments,
  listNotionTreeNodes,
  listNotionPages,
  listPlugins,
  getNotionIntegrationOAuthUrl,
} from '@auw/api-client';
import type {
  CollaborationAttachmentItem,
  CollaborationPageDetail,
  CollaborationPageItem,
  CollaborationProvider,
  CollaborationTreeResponse,
  CollaborationTreeNode,
  PluginInfo,
} from '@auw/api-client';
import type { FileAttachment } from '@auw/workspace-utils';
import { useChatStore } from '@auw/stores';
import { useWorkspaceNavigate } from '@auw/workspace-features';
import { queryKeys } from '@auw/workspace-utils';
import {
  CHAT_ATTACHMENT_MAX_COUNT,
  buildAttachmentCountLimitToast,
  buildAttachmentSizeLimitToast,
  isAttachmentSizeAllowed,
  MODAL_GC_TIME,
  MODAL_STALE_TIME,
} from './attachmentConstraints';
import { useTranslation } from 'react-i18next';

interface CollaborationModalProps {
  open: boolean;
  onClose: () => void;
  initialTab: CollaborationProvider;
  onSelect: (files: FileAttachment[]) => void;
  existingAttachmentCount?: number;
  modalCenterPaddingLeft?: number;
  modalCenterPaddingRight?: number;
}

interface SelectedPageState {
  key: string;
  provider: CollaborationProvider;
  page: CollaborationPageItem;
  includePdf: boolean;
  excludedAttachmentIds: Set<string>;
}

type SelectedPageItem = Pick<SelectedPageState, 'key' | 'provider' | 'page'>;

interface TreeChildrenState {
  nodes: CollaborationTreeNode[];
  nextPageToken: string | null;
  loading: boolean;
  error: string | null;
}

function uuid(): string {
  return crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function formatDate(dateStr?: string, locale?: string): string {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleString(locale);
  } catch {
    return dateStr;
  }
}

function formatFileSize(bytes?: number | null): string {
  if (!bytes || bytes <= 0) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatConfluenceSpaceLabel(raw?: string, personalLabel?: string): string {
  const value = (raw || '').trim();
  if (!value) return '';
  return value.startsWith('~') ? (personalLabel ?? value) : value;
}

function sanitizeFilename(name: string, untitled?: string): string {
  const fallback = untitled ?? '';
  const trimmed = (name || fallback).trim();
  return trimmed.replace(/[\\/:*?"<>|]/g, '_').slice(0, 80) || fallback;
}

export const CollaborationModal: React.FC<CollaborationModalProps> = ({
  open,
  onClose,
  initialTab,
  onSelect,
  existingAttachmentCount = 0,
  modalCenterPaddingLeft = 0,
  modalCenterPaddingRight = 0,
}) => {
  const { openPluginDetail } = useWorkspaceNavigate();
  const addToast = useChatStore((s) => s.addToast);
  const { t, i18n } = useTranslation('widget');

  const [activeTab, setActiveTab] = useState<CollaborationProvider>(initialTab);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  const [selectedPages, setSelectedPages] = useState<Map<string, SelectedPageState>>(new Map());
  const [focusedPage, setFocusedPage] = useState<SelectedPageItem | null>(null);
  const [detailsMap, setDetailsMap] = useState<Map<string, CollaborationPageDetail>>(new Map());
  const detailsMapRef = useRef(detailsMap);
  const [allPageAttachmentsMap, setAllPageAttachmentsMap] = useState<Map<string, CollaborationAttachmentItem[]>>(new Map());
  const allPageAttachmentsMapRef = useRef(allPageAttachmentsMap);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const latestDetailReqRef = useRef(0);
  const [treeChildrenState, setTreeChildrenState] = useState<Record<string, TreeChildrenState>>({});
  const treeChildrenStateRef = useRef(treeChildrenState);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [leafPageNodes, setLeafPageNodes] = useState<Set<string>>(new Set());
  const [attaching, setAttaching] = useState(false);
  const [presetApplying, setPresetApplying] = useState<'pdf-only' | null>(null);

  const listScrollRef = useRef<HTMLDivElement>(null);
  const listSentinelRef = useRef<HTMLDivElement>(null);

  const {
    data: pluginsData,
    isFetching: pluginsFetching,
    refetch: refetchPlugins,
  } = useQuery<PluginInfo[]>({
    queryKey: queryKeys.plugins.list(),
    queryFn: async () => {
      const resp = await listPlugins();
      return resp.plugins;
    },
    enabled: open,
    refetchOnWindowFocus: true,
  });
  const plugins = pluginsData ?? [];
  const pluginsLoading = pluginsFetching && !pluginsData;

  const tabItems: TabItem[] = [
    { id: 'confluence', label: 'Confluence', icon: <TabIcon src="/logo/atlassian.png" alt="Confluence" /> },
    { id: 'notion', label: 'Notion', icon: <TabIcon src="/logo/notion.png" alt="Notion" /> },
  ];

  const isConnected = useCallback((provider: CollaborationProvider): boolean => {
    const pluginId = provider === 'confluence' ? 'atlassian' : 'notion';
    return plugins.some((p) => p.id === pluginId && p.connected);
  }, [plugins]);

  const showAttachmentCountLimitToast = useCallback((skippedCount?: number) => {
    const toast = buildAttachmentCountLimitToast(skippedCount);
    addToast({
      type: 'warning',
      title: toast.title,
      description: toast.description,
    });
  }, [addToast]);

  const showAttachmentSizeLimitToast = useCallback((
    attachments: Array<Pick<CollaborationAttachmentItem, 'name' | 'size'>>,
  ) => {
    if (attachments.length === 0) return;
    const toast = buildAttachmentSizeLimitToast(
      attachments.map((attachment) => ({ name: attachment.name, size: attachment.size })),
    );
    addToast({
      type: 'warning',
      title: toast.title,
      description: toast.description,
    });
  }, [addToast]);

  const getPageSelectionKey = useCallback((provider: CollaborationProvider, pageId: string): string => {
    return `${provider}:${pageId}`;
  }, []);

  const isCollaborationAttachmentSelectable = useCallback((attachment: CollaborationAttachmentItem): boolean => {
    return Boolean((attachment.download_url || '').trim()) && isAttachmentSizeAllowed(attachment.size);
  }, []);

  const normalizedSearchQuery = searchQuery.trim();
  const effectiveSearchQuery = normalizedSearchQuery.length >= 2 ? normalizedSearchQuery : '';

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(effectiveSearchQuery);
    }, 400);
    return () => clearTimeout(timer);
  }, [effectiveSearchQuery]);

  useEffect(() => {
    detailsMapRef.current = detailsMap;
  }, [detailsMap]);

  useEffect(() => {
    allPageAttachmentsMapRef.current = allPageAttachmentsMap;
  }, [allPageAttachmentsMap]);

  useEffect(() => {
    treeChildrenStateRef.current = treeChildrenState;
  }, [treeChildrenState]);

  useEffect(() => {
    if (open) {
      setActiveTab(initialTab);
      setSearchQuery('');
      setDebouncedSearchQuery('');
      setSelectedPages(new Map());
      setFocusedPage(null);
      setDetailsMap(new Map());
      allPageAttachmentsMapRef.current = new Map();
      setAllPageAttachmentsMap(new Map());
      setDetailError(null);
      setTreeChildrenState({});
      setExpandedNodes(new Set());
      setLeafPageNodes(new Set());
      setAttaching(false);
      setPresetApplying(null);
      void refetchPlugins();
    }
  }, [open, initialTab, refetchPlugins]);

  useEffect(() => {
    if (!open) return;
    setSelectedPages(new Map());
    setFocusedPage(null);
    allPageAttachmentsMapRef.current = new Map();
    setAllPageAttachmentsMap(new Map());
    setDetailError(null);
    setTreeChildrenState({});
    setExpandedNodes(new Set());
    setLeafPageNodes(new Set());
    setAttaching(false);
    setPresetApplying(null);
  }, [open, activeTab, debouncedSearchQuery]);

  const rootTreeQuery = useQuery<CollaborationTreeResponse>({
    queryKey: ['collab-tree-root', activeTab],
    enabled: open && !pluginsLoading && isConnected(activeTab) && debouncedSearchQuery.length === 0,
    staleTime: MODAL_STALE_TIME,
    gcTime: MODAL_GC_TIME,
    refetchOnWindowFocus: false,
    queryFn: async ({ signal }) => {
      if (activeTab === 'confluence') {
        return listConfluenceTreeNodes('root', undefined, 80, undefined, { signal });
      }
      return listNotionTreeNodes('root', undefined, undefined, 80, undefined, { signal });
    },
  });

  const pagesQuery = useInfiniteQuery({
    queryKey: ['collab-pages', activeTab, debouncedSearchQuery],
    enabled: open && !pluginsLoading && isConnected(activeTab) && debouncedSearchQuery.length >= 2,
    initialPageParam: undefined as string | undefined,
    staleTime: MODAL_STALE_TIME,
    gcTime: MODAL_GC_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    queryFn: async ({ pageParam, signal }) => {
      if (activeTab === 'confluence') {
        return listConfluencePages(
          debouncedSearchQuery || undefined,
          20,
          pageParam,
          undefined,
          { signal },
        );
      }
      return listNotionPages(
        debouncedSearchQuery || undefined,
        20,
        pageParam,
        { signal },
      );
    },
    getNextPageParam: (lastPage) => lastPage.nextPageToken ?? undefined,
  });

  const pages = useMemo(
    () => pagesQuery.data?.pages.flatMap((p) => p.pages) ?? [],
    [pagesQuery.data],
  );
  const listLoading = pagesQuery.isLoading;
  const listLoadingMore = pagesQuery.isFetchingNextPage;
  const listError = pagesQuery.error instanceof Error ? pagesQuery.error.message : null;

  const fetchPageDetail = useCallback(async (
    provider: CollaborationProvider,
    pageId: string,
  ): Promise<CollaborationPageDetail> => {
    const cacheKey = `${provider}:${pageId}`;
    const cached = detailsMapRef.current.get(cacheKey);
    if (cached) return cached;

    const detail = provider === 'confluence'
      ? await getConfluencePage(pageId)
      : await getNotionPage(pageId);
    setDetailsMap((prev) => {
      const next = new Map(prev);
      next.set(cacheKey, detail);
      return next;
    });
    return detail;
  }, []);

  const handleSelectPage = useCallback(async (provider: CollaborationProvider, page: CollaborationPageItem) => {
    setFocusedPage({
      key: `${provider}:${page.id}`,
      provider,
      page,
    });
    setDetailError(null);

    const requestId = latestDetailReqRef.current + 1;
    latestDetailReqRef.current = requestId;
    setDetailLoading(true);
    try {
      await fetchPageDetail(provider, page.id);
    } catch (e) {
      const msg = e instanceof Error ? e.message : t('collaboration.pageDetailFailed');
      if (latestDetailReqRef.current === requestId) {
        setDetailError(msg);
      }
    } finally {
      if (latestDetailReqRef.current === requestId) {
        setDetailLoading(false);
      }
    }
  }, [fetchPageDetail]);

  const buildTreeStateKey = useCallback((node: CollaborationTreeNode): string => {
    return `${activeTab}:${node.node_type}:${node.id}`;
  }, [activeTab]);

  const fetchTreeChildren = useCallback(async (
    node: CollaborationTreeNode,
    append: boolean = false,
  ) => {
    const stateKey = buildTreeStateKey(node);
    const rowKey = `${activeTab}:${node.node_type}:${node.id}`;
    const currentState = treeChildrenStateRef.current[stateKey];
    const token = append ? currentState?.nextPageToken ?? undefined : undefined;
    const existingNodesCount = append ? (currentState?.nodes?.length ?? 0) : 0;

    setTreeChildrenState((prev) => ({
      ...prev,
      [stateKey]: {
        nodes: append ? (prev[stateKey]?.nodes ?? []) : (prev[stateKey]?.nodes ?? []),
        nextPageToken: prev[stateKey]?.nextPageToken ?? null,
        loading: true,
        error: null,
      },
    }));

    try {
      let payload;
      if (activeTab === 'confluence') {
        if (node.node_type === 'space') {
          payload = await listConfluenceTreeNodes('space', node.id, 40, token);
        } else {
          payload = await listConfluenceTreeNodes('page', node.id, 40, token);
        }
      } else if (node.node_type === 'database') {
        payload = await listNotionTreeNodes('database', node.id, undefined, 40, token);
      } else {
        payload = await listNotionTreeNodes('page', node.id, undefined, 40, token);
      }

      setTreeChildrenState((prev) => {
        const oldNodes = append ? (prev[stateKey]?.nodes ?? []) : [];
        const mergedNodes = [...oldNodes, ...(payload.nodes ?? [])];
        const dedupedNodes: CollaborationTreeNode[] = [];
        const seenNodeKeys = new Set<string>();
        for (const mergedNode of mergedNodes) {
          const nodeKey = `${mergedNode.node_type}:${mergedNode.id}`;
          if (seenNodeKeys.has(nodeKey)) continue;
          seenNodeKeys.add(nodeKey);
          dedupedNodes.push(mergedNode);
        }
        return {
          ...prev,
          [stateKey]: {
            nodes: dedupedNodes,
            nextPageToken: payload.nextPageToken ?? null,
            loading: false,
            error: null,
          },
        };
      });

      if (node.node_type === 'page') {
        const loadedNodesCount = Array.isArray(payload.nodes) ? payload.nodes.length : 0;
        const totalChildrenCount = existingNodesCount + loadedNodesCount;
        const hasMoreChildren = Boolean(payload.nextPageToken);
        const isLeafPage = totalChildrenCount === 0 && !hasMoreChildren;

        setLeafPageNodes((prev) => {
          const next = new Set(prev);
          if (isLeafPage) next.add(rowKey);
          else next.delete(rowKey);
          return next;
        });

        if (isLeafPage) {
          setExpandedNodes((prev) => {
            if (!prev.has(rowKey)) return prev;
            const next = new Set(prev);
            next.delete(rowKey);
            return next;
          });
        }
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : t('collaboration.treeLoadFailed');
      setTreeChildrenState((prev) => ({
        ...prev,
        [stateKey]: {
          nodes: prev[stateKey]?.nodes ?? [],
          nextPageToken: prev[stateKey]?.nextPageToken ?? null,
          loading: false,
          error: msg,
        },
      }));
    }
  }, [activeTab, buildTreeStateKey]);

  const toggleExpandNode = useCallback((node: CollaborationTreeNode) => {
    const expandKey = `${activeTab}:${node.node_type}:${node.id}`;
    const isLeafPage = node.node_type === 'page' && (leafPageNodes.has(expandKey) || !node.has_children);
    if (isLeafPage || !node.has_children) return;

    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(expandKey)) next.delete(expandKey);
      else next.add(expandKey);
      return next;
    });

    const stateKey = buildTreeStateKey(node);
    const state = treeChildrenStateRef.current[stateKey];
    if (!state || (!state.loading && state.nodes.length === 0 && !state.error)) {
      void fetchTreeChildren(node, false);
    }
  }, [activeTab, buildTreeStateKey, fetchTreeChildren, leafPageNodes]);

  useEffect(() => {
    if (!listSentinelRef.current || !listScrollRef.current) return;
    if (debouncedSearchQuery.length < 2) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting
          && pagesQuery.hasNextPage
          && !pagesQuery.isFetchingNextPage
        ) {
          void pagesQuery.fetchNextPage();
        }
      },
      { root: listScrollRef.current, threshold: 0.1 },
    );
    observer.observe(listSentinelRef.current);
    return () => observer.disconnect();
  }, [debouncedSearchQuery.length, pagesQuery.hasNextPage, pagesQuery.isFetchingNextPage, pagesQuery.fetchNextPage]);

  const nodeToPageItem = useCallback((node: CollaborationTreeNode): CollaborationPageItem => {
    return {
      id: node.id,
      title: node.title,
      snippet: node.subtitle,
      space_name: node.space_key || node.subtitle || '',
      url: node.url,
      last_edited_at: node.last_edited_at,
    };
  }, []);

  const handleOpenPageLink = useCallback((url?: string) => {
    const targetUrl = (url || '').trim();
    if (!targetUrl) return;
    window.open(targetUrl, '_blank', 'noopener,noreferrer');
  }, []);

  const focusedPageKey = focusedPage ? `${focusedPage.provider}:${focusedPage.page.id}` : null;
  const focusedPageSelection = focusedPageKey ? selectedPages.get(focusedPageKey) ?? null : null;
  const focusedPageSelected = focusedPageKey ? selectedPages.has(focusedPageKey) : false;

  const setFocusedPageChecked = useCallback(async (checked: boolean) => {
    if (!focusedPage || !focusedPageKey) return;

    if (!checked) {
      setSelectedPages((prev) => {
        const next = new Map(prev);
        next.delete(focusedPageKey);
        return next;
      });
      return;
    }

    try {
      const allAttachments = await fetchAndCacheAllPageAttachments(focusedPage.provider, focusedPage.page.id);
      const disabledAttachmentIds = buildDisabledAttachmentIds(allAttachments);
      const oversizedAttachments = allAttachments.filter((attachment) => !isAttachmentSizeAllowed(attachment.size));
      const nextState: SelectedPageState = {
        key: focusedPageKey,
        provider: focusedPage.provider,
        page: focusedPage.page,
        includePdf: true,
        excludedAttachmentIds: disabledAttachmentIds,
      };
      const nextFileCount = getSelectedFileCountForPageState(nextState);

      if (!canApplyFocusedSelectionCount(nextFileCount)) {
        showAttachmentCountLimitToast();
        return;
      }

      if (oversizedAttachments.length > 0) {
        showAttachmentSizeLimitToast(oversizedAttachments);
      }

      setSelectedPages((prev) => {
        const next = new Map(prev);
        next.set(focusedPageKey, nextState);
        return next;
      });
    } catch (e) {
      const msg = e instanceof Error ? e.message : t('collaboration.pageSelectionFailed');
      addToast({
        type: 'error',
        title: t('collaboration.selectionFailed'),
        description: msg,
      });
    }
  }, [
    focusedPage,
    focusedPageKey,
    fetchAndCacheAllPageAttachments,
    buildDisabledAttachmentIds,
    getSelectedFileCountForPageState,
    canApplyFocusedSelectionCount,
    showAttachmentCountLimitToast,
    showAttachmentSizeLimitToast,
    addToast,
  ]);

  const setFocusedPdfSelected = useCallback(async (checked: boolean) => {
    if (!focusedPage || !focusedPageKey) return;

    try {
      const allAttachments = await fetchAndCacheAllPageAttachments(focusedPage.provider, focusedPage.page.id);
      const disabledAttachmentIds = buildDisabledAttachmentIds(allAttachments);
      const current = selectedPages.get(focusedPageKey);
      const nextState: SelectedPageState = current
        ? {
            ...current,
            includePdf: checked,
            excludedAttachmentIds: new Set([...current.excludedAttachmentIds, ...disabledAttachmentIds]),
          }
        : {
            key: focusedPageKey,
            provider: focusedPage.provider,
            page: focusedPage.page,
            includePdf: checked,
            excludedAttachmentIds: disabledAttachmentIds,
          };
      const nextFileCount = getSelectedFileCountForPageState(nextState);

      if (checked && !canApplyFocusedSelectionCount(nextFileCount)) {
        showAttachmentCountLimitToast();
        return;
      }

      setSelectedPages((prev) => {
        const next = new Map(prev);
        if (nextFileCount === 0) {
          next.delete(focusedPageKey);
        } else {
          next.set(focusedPageKey, nextState);
        }
        return next;
      });
    } catch (e) {
      const msg = e instanceof Error ? e.message : t('collaboration.pdfSelectionFailed');
      addToast({
        type: 'error',
        title: t('collaboration.selectionFailed'),
        description: msg,
      });
    }
  }, [
    focusedPage,
    focusedPageKey,
    fetchAndCacheAllPageAttachments,
    buildDisabledAttachmentIds,
    selectedPages,
    getSelectedFileCountForPageState,
    canApplyFocusedSelectionCount,
    showAttachmentCountLimitToast,
    addToast,
  ]);

  const setFocusedAttachmentSelected = useCallback(async (
    attachment: CollaborationAttachmentItem,
    checked: boolean,
  ) => {
    if (!focusedPage || !focusedPageKey) return;

    if (checked && !isCollaborationAttachmentSelectable(attachment)) {
      if (!isAttachmentSizeAllowed(attachment.size)) {
        showAttachmentSizeLimitToast([attachment]);
      } else {
        addToast({
          type: 'warning',
          title: t('collaboration.notSelectable'),
          description: t('collaboration.cannotSelectAttachment'),
        });
      }
      return;
    }

    try {
      const allAttachments = await fetchAndCacheAllPageAttachments(focusedPage.provider, focusedPage.page.id);
      const disabledAttachmentIds = buildDisabledAttachmentIds(allAttachments);
      const current = selectedPages.get(focusedPageKey);
      const excludedAttachmentIds = current
        ? new Set([...current.excludedAttachmentIds, ...disabledAttachmentIds])
        : new Set(allAttachments.map((item) => item.id));

      if (checked) {
        excludedAttachmentIds.delete(attachment.id);
      } else {
        excludedAttachmentIds.add(attachment.id);
      }

      const nextState: SelectedPageState = {
        key: focusedPageKey,
        provider: focusedPage.provider,
        page: focusedPage.page,
        includePdf: current?.includePdf ?? false,
        excludedAttachmentIds,
      };
      const nextFileCount = getSelectedFileCountForPageState(nextState);

      if (checked && !canApplyFocusedSelectionCount(nextFileCount)) {
        showAttachmentCountLimitToast();
        return;
      }

      setSelectedPages((prev) => {
        const next = new Map(prev);
        if (nextFileCount === 0) {
          next.delete(focusedPageKey);
        } else {
          next.set(focusedPageKey, nextState);
        }
        return next;
      });
    } catch (e) {
      const msg = e instanceof Error ? e.message : t('collaboration.attachmentSelectionFailed');
      addToast({
        type: 'error',
        title: t('collaboration.selectionFailed'),
        description: msg,
      });
    }
  }, [
    focusedPage,
    focusedPageKey,
    fetchAndCacheAllPageAttachments,
    buildDisabledAttachmentIds,
    selectedPages,
    isCollaborationAttachmentSelectable,
    getSelectedFileCountForPageState,
    canApplyFocusedSelectionCount,
    showAttachmentCountLimitToast,
    showAttachmentSizeLimitToast,
    addToast,
  ]);

  const fetchAllPageAttachments = useCallback(async (
    provider: CollaborationProvider,
    pageId: string,
  ): Promise<CollaborationAttachmentItem[]> => {
    const all: CollaborationAttachmentItem[] = [];
    let nextPageToken: string | undefined;
    for (let i = 0; i < 20; i += 1) {
      const payload = provider === 'confluence'
        ? await listConfluencePageAttachments(pageId, 100, nextPageToken)
        : await listNotionPageAttachments(pageId, 100, nextPageToken);
      if (Array.isArray(payload.attachments) && payload.attachments.length > 0) {
        all.push(...payload.attachments);
      }
      nextPageToken = payload.nextPageToken ?? undefined;
      if (!nextPageToken) break;
    }
    return all;
  }, []);

  async function fetchAndCacheAllPageAttachments(
    provider: CollaborationProvider,
    pageId: string,
  ): Promise<CollaborationAttachmentItem[]> {
    const pageKey = getPageSelectionKey(provider, pageId);
    const cached = allPageAttachmentsMapRef.current.get(pageKey);
    if (cached) return cached;

    const all = await fetchAllPageAttachments(provider, pageId);
    setAllPageAttachmentsMap((prev) => {
      const next = new Map(prev);
      next.set(pageKey, all);
      allPageAttachmentsMapRef.current = next;
      return next;
    });
    return all;
  }

  function buildDisabledAttachmentIds(attachments: CollaborationAttachmentItem[]): Set<string> {
    return new Set(
      attachments
        .filter((attachment) => !isCollaborationAttachmentSelectable(attachment))
        .map((attachment) => attachment.id),
    );
  }

  function getSelectedFileCountForPageState(pageState: SelectedPageState | null | undefined): number {
    if (!pageState) return 0;
    const attachments = allPageAttachmentsMapRef.current.get(pageState.key) ?? [];
    let count = pageState.includePdf ? 1 : 0;
    for (const attachment of attachments) {
      if (!isCollaborationAttachmentSelectable(attachment)) continue;
      if (pageState.excludedAttachmentIds.has(attachment.id)) continue;
      count += 1;
    }
    return count;
  }

  const selectedFileCount = useMemo(() => {
    let count = 0;
    for (const item of selectedPages.values()) {
      count += getSelectedFileCountForPageState(item);
    }
    return count;
  }, [selectedPages, allPageAttachmentsMap]);

  const focusedPageSelectedFileCount = useMemo(() => {
    return getSelectedFileCountForPageState(focusedPageSelection);
  }, [focusedPageSelection, allPageAttachmentsMap]);

  const remainingSlotsForFocusedPage = useMemo(() => {
    return Math.max(
      0,
      CHAT_ATTACHMENT_MAX_COUNT - existingAttachmentCount - (selectedFileCount - focusedPageSelectedFileCount),
    );
  }, [existingAttachmentCount, focusedPageSelectedFileCount, selectedFileCount]);

  function canApplyFocusedSelectionCount(nextFocusedFileCount: number): boolean {
    return nextFocusedFileCount <= remainingSlotsForFocusedPage;
  }

  const selectedSummary = useMemo(() => {
    if (selectedFileCount === 0) {
      return existingAttachmentCount > 0
        ? t('collaboration.currentAttached', { count: existingAttachmentCount, max: CHAT_ATTACHMENT_MAX_COUNT })
        : t('collaboration.selectFiles');
    }
    return t('collaboration.filesSelected', { count: selectedFileCount, total: existingAttachmentCount + selectedFileCount, max: CHAT_ATTACHMENT_MAX_COUNT });
  }, [existingAttachmentCount, selectedFileCount, t]);

  const applyFocusedAssetPreset = useCallback(async (
    preset: 'all' | 'pdf-only' | 'attachments-only',
  ) => {
    if (!focusedPage || !focusedPageKey) return;

    if (preset === 'pdf-only') {
      setPresetApplying('pdf-only');
    }

    try {
      const allAttachments = await fetchAndCacheAllPageAttachments(focusedPage.provider, focusedPage.page.id);
      const disabledAttachmentIds = buildDisabledAttachmentIds(allAttachments);
      const oversizedAttachments = allAttachments.filter((attachment) => !isAttachmentSizeAllowed(attachment.size));
      const nextState: SelectedPageState = {
        key: focusedPageKey,
        provider: focusedPage.provider,
        page: focusedPage.page,
        includePdf: preset !== 'attachments-only',
        excludedAttachmentIds:
          preset === 'pdf-only'
            ? new Set(allAttachments.map((attachment) => attachment.id))
            : disabledAttachmentIds,
      };
      const nextFileCount = getSelectedFileCountForPageState(nextState);

      if (nextFileCount === 0) {
        addToast({
          type: 'warning',
          title: t('collaboration.noFilesSelected'),
          description: preset === 'attachments-only'
            ? t('collaboration.noSelectableAttachments')
            : t('collaboration.selectPdfOrAttachments'),
        });
        return;
      }

      if (!canApplyFocusedSelectionCount(nextFileCount)) {
        showAttachmentCountLimitToast();
        return;
      }

      if (oversizedAttachments.length > 0 && preset !== 'pdf-only') {
        showAttachmentSizeLimitToast(oversizedAttachments);
      }

      setSelectedPages((prev) => {
        const next = new Map(prev);
        next.set(focusedPageKey, nextState);
        return next;
      });
    } catch (e) {
      const msg = e instanceof Error ? e.message : t('collaboration.quickSelectFailed');
      addToast({
        type: 'error',
        title: t('collaboration.selectionFailed'),
        description: msg,
      });
    } finally {
      if (preset === 'pdf-only') {
        setPresetApplying(null);
      }
    }
  }, [
    focusedPage,
    focusedPageKey,
    fetchAndCacheAllPageAttachments,
    buildDisabledAttachmentIds,
    getSelectedFileCountForPageState,
    canApplyFocusedSelectionCount,
    showAttachmentCountLimitToast,
    showAttachmentSizeLimitToast,
    addToast,
  ]);

  const handleAttach = useCallback(async () => {
    if (selectedFileCount === 0 || attaching) return;

    setAttaching(true);
    try {
      const selectedItems = Array.from(selectedPages.values());
      const files: FileAttachment[] = [];

      for (const item of selectedItems) {
        const detail = await fetchPageDetail(item.provider, item.page.id);
        const pageAssetSelection = item;
        const pageTitle = sanitizeFilename(detail.title || item.page.title, t('collaboration.untitled'));

        if (pageAssetSelection.includePdf) {
          const pdfUrl = getCollaborationPagePdfUrl(item.provider, item.page.id);
          files.push({
            id: uuid(),
            name: `${pageTitle}.pdf`,
            type: 'application/pdf',
            size: 0,
            remoteUrl: pdfUrl,
            collaborationSource: item.provider,
            collaborationPageId: item.page.id,
            collaborationItemId: item.page.id,
          });
        }

        const pageAttachments = await fetchAndCacheAllPageAttachments(item.provider, item.page.id);
        for (const att of pageAttachments) {
          if (!isCollaborationAttachmentSelectable(att)) continue;
          if (pageAssetSelection.excludedAttachmentIds.has(att.id)) continue;
          const downloadUrl = (att.download_url || '').trim();
          if (!downloadUrl) continue;
          const fallbackName = sanitizeFilename(att.name || `attachment-${att.id}`, t('collaboration.untitled'));
          files.push({
            id: uuid(),
            name: fallbackName,
            type: att.media_type || 'application/octet-stream',
            size: typeof att.size === 'number' ? att.size : 0,
            remoteUrl: downloadUrl,
            collaborationSource: item.provider,
            collaborationPageId: item.page.id,
            collaborationItemId: att.id,
          });
        }
      }

      if (files.length === 0) {
        addToast({
          type: 'warning',
          title: t('collaboration.noFilesSelected'),
          description: t('collaboration.selectPdfOrAttachments'),
        });
        return;
      }

      onSelect(files);
      onClose();
    } catch (e) {
      const msg = e instanceof Error ? e.message : t('collaboration.pageAttachFailed');
      addToast({
        type: 'error',
        title: t('collaboration.attachFailed'),
        description: msg,
      });
    } finally {
      setAttaching(false);
    }
  }, [
    selectedPages,
    selectedFileCount,
    attaching,
    fetchPageDetail,
    fetchAndCacheAllPageAttachments,
    isCollaborationAttachmentSelectable,
    addToast,
    onSelect,
    onClose,
  ]);

  const attachmentsQuery = useQuery<CollaborationAttachmentItem[]>({
    queryKey: ['collab-attachments', focusedPage?.provider, focusedPage?.page.id],
    enabled: open && !!focusedPage,
    staleTime: MODAL_STALE_TIME,
    gcTime: MODAL_GC_TIME,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<CollaborationAttachmentItem[]> => {
      if (!focusedPage) return [];
      return fetchAndCacheAllPageAttachments(focusedPage.provider, focusedPage.page.id);
    },
  });

  const handleOpenAttachment = useCallback(async (downloadUrl?: string) => {
    if (!downloadUrl) return;
    const openedTab = window.open('about:blank', '_blank');
    try {
      const openTarget = await getCollaborationAttachmentOpenTarget(downloadUrl);
      if (openedTab) {
        openedTab.location.href = openTarget.url;
      } else {
        window.open(openTarget.url, '_blank');
      }
      if (openTarget.revokeAfterMs) {
        window.setTimeout(() => URL.revokeObjectURL(openTarget.url), openTarget.revokeAfterMs);
      }
    } catch (e) {
      if (openedTab && !openedTab.closed) {
        openedTab.close();
      }
      const msg = e instanceof Error ? e.message : t('collaboration.cannotOpenAttachment');
      addToast({
        type: 'error',
        title: t('collaboration.openAttachmentFailed'),
        description: msg,
      });
    }
  }, [addToast]);


  const handleGoToPlugin = useCallback(() => {
    const pluginId = activeTab === 'confluence' ? 'atlassian' : 'notion';
    openPluginDetail(pluginId);
    onClose();
  }, [activeTab, onClose, openPluginDetail]);

  const activeDetail = useMemo(() => {
    if (!focusedPage) return null;
    return detailsMap.get(`${focusedPage.provider}:${focusedPage.page.id}`) ?? null;
  }, [detailsMap, focusedPage]);

  const renderNotConnected = () => {
    const isConfluence = activeTab === 'confluence';
    return (
      <NotConnectedArea>
        <EmptyState
          icon={
            <CollaborationLogoWrap>
              <img
                src={isConfluence ? '/logo/atlassian.png' : '/logo/notion.png'}
                alt={isConfluence ? 'Confluence' : 'Notion'}
                width={48}
                height={48}
              />
            </CollaborationLogoWrap>
          }
          title={t('collaboration.notConnected', { provider: isConfluence ? 'Confluence' : 'Notion' })}
          description={t('collaboration.connectFromPlugin')}
          action={
            <Button variant="primary" size="md" onClick={handleGoToPlugin}>
              {t('collaboration.connect')}
            </Button>
          }
        />
      </NotConnectedArea>
    );
  };

  const renderListSkeleton = () => (
    <SkeletonArea>
      {Array.from({ length: 6 }).map((_, idx) => (
        <SkeletonRow key={idx}>
          <SkeletonLine width="16px" height="16px" />
          <SkeletonLine width="60%" height="13px" />
          <SkeletonLine width="35%" height="11px" />
        </SkeletonRow>
      ))}
    </SkeletonArea>
  );

  const renderTreeNodes = useCallback((nodes: CollaborationTreeNode[], depth: number = 0): React.ReactNode => {
    return nodes.map((node) => {
      const rowKey = `${activeTab}:${node.node_type}:${node.id}`;
      const childrenStateKey = buildTreeStateKey(node);
      const childrenState = treeChildrenState[childrenStateKey];
      const isExpanded = expandedNodes.has(rowKey);
      const isPageNode = node.node_type === 'page';
      const isLeafPageNode = isPageNode && (leafPageNodes.has(rowKey) || !node.has_children);
      const canExpand = node.has_children && !isLeafPageNode;
      const pageFromNode = isPageNode ? nodeToPageItem(node) : null;
      const subtitleLabel =
        activeTab === 'confluence'
          ? formatConfluenceSpaceLabel(node.subtitle, t('collaboration.personalSpace'))
          : (node.subtitle || '');
      const isActive = focusedPage?.provider === activeTab && focusedPage?.page.id === node.id;

      return (
        <React.Fragment key={rowKey}>
          <PageRow
            $active={Boolean(isActive)}
            onClick={() => {
              if (isPageNode && pageFromNode) {
                void handleSelectPage(activeTab, pageFromNode);
              } else if (canExpand) {
                toggleExpandNode(node);
              }
            }}
          >
            <TreeIndent $depth={depth} />
            <ExpandToggle
              onClick={(e) => {
                e.stopPropagation();
                if (!canExpand) return;
                toggleExpandNode(node);
              }}
            >
              {canExpand
                ? (isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />)
                : <TreeLeafSpacer />}
            </ExpandToggle>
            {isPageNode ? (
              <TreeIconCell>
                <FileText size={14} />
              </TreeIconCell>
            ) : (
              <TreeIconCell>
                {isExpanded ? <FolderOpen size={14} /> : <Folder size={14} />}
              </TreeIconCell>
            )}
            <PageInfo>
              <PageTitleRow>
                <PageTitle>{node.title || t('collaboration.untitled')}</PageTitle>
                {node.url && (
                  <PageLinkButton
                    type="button"
                    title={t('collaboration.openInNewTab')}
                    aria-label={t('collaboration.openInNewTab')}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenPageLink(node.url);
                    }}
                  >
                    <ExternalLink size={12} />
                  </PageLinkButton>
                )}
              </PageTitleRow>
              <PageMeta>
                {subtitleLabel ? `${subtitleLabel} · ` : ''}
                {formatDate(node.last_edited_at, i18n.language)}
              </PageMeta>
            </PageInfo>
          </PageRow>

          {isExpanded && (
            <>
              {childrenState?.loading && (
                <TreeStatusRow>
                  <Loader size={14} className="spin" />
                  <span>{t('collaboration.loadingChildren')}</span>
                </TreeStatusRow>
              )}
              {childrenState?.error && (
                <TreeStatusRow>
                  <RefreshCw size={14} />
                  <span>{childrenState.error}</span>
                </TreeStatusRow>
              )}
              {childrenState?.nodes?.length ? renderTreeNodes(childrenState.nodes, depth + 1) : null}
              {childrenState?.nextPageToken && !childrenState.loading && (
                <TreeLoadMoreButton
                  onClick={() => {
                    void fetchTreeChildren(node, true);
                  }}
                >
                  {t('collaboration.loadMoreChildren')}
                </TreeLoadMoreButton>
              )}
            </>
          )}
        </React.Fragment>
      );
    });
  }, [
    activeTab,
    buildTreeStateKey,
    expandedNodes,
    fetchTreeChildren,
    focusedPage?.page.id,
    focusedPage?.provider,
    handleSelectPage,
    handleOpenPageLink,
    leafPageNodes,
    nodeToPageItem,
    toggleExpandNode,
    treeChildrenState,
  ]);

  const renderListPane = () => {
    const isSearchMode = debouncedSearchQuery.length >= 2;
    const rootNodes = rootTreeQuery.data?.nodes ?? [];
    const rootTreeError = rootTreeQuery.error instanceof Error ? rootTreeQuery.error.message : null;

    return (
      <ListPane>
        <SearchArea>
          <Input
            variant="search"
            inputSize="sm"
            placeholder={t('collaboration.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </SearchArea>

        {activeTab === 'notion' && !rootTreeError?.includes('401') && (
          <NotionAccessBar>
            <NotionAccessText>
              {t('collaboration.notionAccessHint') || '접근 가능한 페이지만 표시됩니다'}
            </NotionAccessText>
            <div style={{ display: 'flex', gap: 4 }}>
              <NotionAccessButton
                type="button"
                title={t('collaboration.notionRefreshTree') || '새로고침'}
                onClick={() => { void rootTreeQuery.refetch(); }}
                disabled={rootTreeQuery.isFetching}
                style={{ opacity: rootTreeQuery.isFetching ? 0.5 : 1 }}
              >
                <RefreshCw size={11} className={rootTreeQuery.isFetching ? 'spin' : ''} />
              </NotionAccessButton>
              <NotionAccessButton
                type="button"
                onClick={async () => {
                  try {
                    const redirectUri = `${window.location.origin}${window.location.pathname}`;
                    const result = await getNotionIntegrationOAuthUrl(redirectUri);
                    if (result.authorization_url) {
                      const popup = window.open(result.authorization_url, '_blank', 'width=600,height=700');
                      if (popup) {
                        const pollTimer = window.setInterval(() => {
                          if (popup.closed) {
                            clearInterval(pollTimer);
                            void rootTreeQuery.refetch();
                          }
                        }, 1000);
                        window.setTimeout(() => clearInterval(pollTimer), 5 * 60 * 1000);
                      }
                    }
                  } catch (e) {
                    console.error('[CollaborationModal] Notion page access manage failed:', e);
                  }
                }}
              >
                {t('collaboration.notionManageAccess') || '페이지 접근 관리'}
              </NotionAccessButton>
            </div>
          </NotionAccessBar>
        )}

        <ListScrollArea ref={listScrollRef}>
          {isSearchMode ? (
            <>
              {listLoading && renderListSkeleton()}
              {!listLoading && listError && (
                <EmptyState
                  icon={<RefreshCw size={30} />}
                  title={t('collaboration.errorOccurred')}
                  description={listError}
                  action={(
                    <Button variant="secondary" size="sm" onClick={() => { void pagesQuery.refetch(); }}>
                      {t('collaboration.retry')}
                    </Button>
                  )}
                />
              )}
              {!listLoading && !listError && pages.length === 0 && (
                <EmptyState
                  icon={<FileText size={30} />}
                  title={t('collaboration.noSearchResults')}
                />
              )}
              {!listLoading && !listError && pages.map((page) => {
                const key = `${activeTab}:${page.id}`;
                const isActive = focusedPage?.provider === activeTab && focusedPage?.page.id === page.id;
                return (
                  <PageRow
                    key={key}
                    $active={Boolean(isActive)}
                    onClick={() => { void handleSelectPage(activeTab, page); }}
                  >
                    <TreeIconCell>
                      <FileText size={14} />
                    </TreeIconCell>
                    <PageInfo>
                      <PageTitleRow>
                        <PageTitle>{page.title || t('collaboration.untitled')}</PageTitle>
                        {page.url && (
                          <PageLinkButton
                            type="button"
                            title={t('collaboration.openInNewTab')}
                            aria-label={t('collaboration.openInNewTab')}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenPageLink(page.url);
                            }}
                          >
                            <ExternalLink size={12} />
                          </PageLinkButton>
                        )}
                      </PageTitleRow>
                      <PageMeta>
                        {activeTab === 'confluence' && page.space_name
                          ? `${formatConfluenceSpaceLabel(page.space_name, t('collaboration.personalSpace'))} · `
                          : ''}
                        {formatDate(page.last_edited_at, i18n.language)}
                      </PageMeta>
                    </PageInfo>
                  </PageRow>
                );
              })}

              {listLoadingMore && (
                <LoadMoreIndicator>
                  <Loader size={15} className="spin" />
                  <span>{t('collaboration.loadingMore')}</span>
                </LoadMoreIndicator>
              )}
              <div ref={listSentinelRef} style={{ height: 1 }} />
            </>
          ) : (
            <>
              {rootTreeQuery.isLoading && renderListSkeleton()}
              {!rootTreeQuery.isLoading && rootTreeError && (
                rootTreeError.includes('401') && activeTab === 'notion' ? (
                  <EmptyState
                    icon={<FileText size={30} />}
                    title={t('collaboration.notionFileAuthRequired')}
                    description={t('collaboration.notionFileAuthDescription')}
                    action={(
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={async () => {
                          try {
                            const redirectUri = `${window.location.origin}${window.location.pathname}`;
                            const result = await getNotionIntegrationOAuthUrl(redirectUri);
                            if (result.authorization_url) {
                              const popup = window.open(result.authorization_url, '_blank', 'width=600,height=700');
                              if (popup) {
                                const pollTimer = window.setInterval(() => {
                                  if (popup.closed) {
                                    clearInterval(pollTimer);
                                    void rootTreeQuery.refetch();
                                  }
                                }, 1000);
                                window.setTimeout(() => clearInterval(pollTimer), 5 * 60 * 1000);
                              }
                            }
                          } catch (e) {
                            console.error('[CollaborationModal] Notion Integration OAuth failed:', e);
                          }
                        }}
                      >
                        {t('collaboration.notionFileAuthConnect')}
                      </Button>
                    )}
                  />
                ) : (
                  <EmptyState
                    icon={<RefreshCw size={30} />}
                    title={t('collaboration.treeLoadingFailed')}
                    description={rootTreeError}
                    action={(
                      <Button variant="secondary" size="sm" onClick={() => { void rootTreeQuery.refetch(); }}>
                        {t('collaboration.retry')}
                      </Button>
                    )}
                  />
                )
              )}
              {!rootTreeQuery.isLoading && !rootTreeError && rootNodes.length === 0 && (
                <EmptyState
                  icon={<FileText size={30} />}
                  title={t('collaboration.noItemsToShow')}
                  description={t('collaboration.noItemsDescription')}
                />
              )}
              {!rootTreeQuery.isLoading && !rootTreeError && renderTreeNodes(rootNodes, 0)}
            </>
          )}
        </ListScrollArea>
      </ListPane>
    );
  };

  const renderDetailPane = () => {
    const attachments = attachmentsQuery.data ?? [];
    const attachmentError = attachmentsQuery.error instanceof Error ? attachmentsQuery.error.message : null;
    const excludedAttachmentIds = focusedPageSelection?.excludedAttachmentIds ?? new Set<string>();
    const isPdfSelected = focusedPageSelection?.includePdf ?? false;
    const selectableAttachmentIds = attachments
      .filter((attachment) => isCollaborationAttachmentSelectable(attachment))
      .map((attachment) => attachment.id);
    const isAllPreset = focusedPageSelected
      && isPdfSelected
      && selectableAttachmentIds.every((id) => !excludedAttachmentIds.has(id));
    const isAttachmentOnlyPreset = focusedPageSelected
      && !isPdfSelected
      && selectableAttachmentIds.length > 0
      && selectableAttachmentIds.every((id) => !excludedAttachmentIds.has(id));
    const isPdfOnlyPreset = focusedPageSelected
      && isPdfSelected
      && selectableAttachmentIds.every((id) => excludedAttachmentIds.has(id));
    const pdfSelectionDisabled = !isPdfSelected && !canApplyFocusedSelectionCount(focusedPageSelectedFileCount + 1);

    return (
      <DetailPane>
        {!focusedPage && (
          <DetailEmpty>
            <FileText size={36} />
            <span>{t('collaboration.selectAPage')}</span>
          </DetailEmpty>
        )}

        {focusedPage && detailLoading && (
          <DetailLoading>
            <RingSpinner size={22} />
          </DetailLoading>
        )}

        {focusedPage && !detailLoading && detailError && (
          <DetailErrorWrap>
            <EmptyState
              icon={<RefreshCw size={28} />}
              title={t('collaboration.detailLoadFailed')}
              description={detailError}
            />
          </DetailErrorWrap>
        )}

        {focusedPage && !detailLoading && !detailError && activeDetail && (
          <DetailContent>
            <DetailTitle>{activeDetail.title || t('collaboration.untitled')}</DetailTitle>
            <DetailMeta>
              {focusedPage.provider === 'confluence' && activeDetail.space_name
                ? `${formatConfluenceSpaceLabel(activeDetail.space_name, t('collaboration.personalSpace'))} · `
                : ''}
              {formatDate(activeDetail.last_edited_at, i18n.language)}
            </DetailMeta>

            <PageSelectRow
              $active={focusedPageSelected}
              onClick={(e) => {
                const target = e.target as HTMLElement;
                if (target.closest('label, input, button, a')) return;
                if (!focusedPage) return;
                void setFocusedPageChecked(!focusedPageSelected);
              }}
            >
              <CheckboxCell onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  checked={focusedPageSelected}
                  onChange={(checked) => {
                    if (!focusedPage) return;
                    void setFocusedPageChecked(checked);
                  }}
                  size="sm"
                />
              </CheckboxCell>
              <PageSelectInfo>
                <PageSelectTitle>{t('collaboration.selectThisPage')}</PageSelectTitle>
                <PageSelectMeta>{t('collaboration.selectPageHint')}</PageSelectMeta>
              </PageSelectInfo>
            </PageSelectRow>

            <AttachmentCards>
              <AttachmentCardsTitle>
                <Paperclip size={13} />
                {t('collaboration.attachmentItems')}
              </AttachmentCardsTitle>
              <AttachmentQuickActions>
                <AttachmentQuickButton
                  type="button"
                  $active={isAllPreset}
                  disabled={presetApplying === 'pdf-only'}
                  onClick={() => { void applyFocusedAssetPreset('all'); }}
                >
                  {t('collaboration.selectAll')}
                </AttachmentQuickButton>
                <AttachmentQuickButton
                  type="button"
                  $active={isPdfOnlyPreset}
                  disabled={presetApplying === 'pdf-only'}
                  onClick={() => { void applyFocusedAssetPreset('pdf-only'); }}
                >
                  {presetApplying === 'pdf-only' ? t('collaboration.applying') : t('collaboration.pdfOnly')}
                </AttachmentQuickButton>
                <AttachmentQuickButton
                  type="button"
                  $active={isAttachmentOnlyPreset}
                  disabled={presetApplying === 'pdf-only'}
                  onClick={() => { void applyFocusedAssetPreset('attachments-only'); }}
                >
                  {t('collaboration.attachmentsOnly')}
                </AttachmentQuickButton>
              </AttachmentQuickActions>

              <AttachmentCard
                $selected={isPdfSelected}
                $disabled={pdfSelectionDisabled}
                onClick={(e) => {
                  const target = e.target as HTMLElement;
                  if (target.closest('label, input, button, a')) return;
                  void setFocusedPdfSelected(!isPdfSelected);
                }}
                title={pdfSelectionDisabled ? t('collaboration.maxAttachments', { max: CHAT_ATTACHMENT_MAX_COUNT }) : undefined}
              >
                <CheckboxCell onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={isPdfSelected}
                    onChange={(checked) => { void setFocusedPdfSelected(checked); }}
                    size="sm"
                    disabled={pdfSelectionDisabled}
                  />
                </CheckboxCell>
                <AttachmentCardIcon><FileText size={14} /></AttachmentCardIcon>
                <AttachmentCardInfo>
                  <AttachmentCardName>{t('collaboration.pagePdf')}</AttachmentCardName>
                  <AttachmentCardMeta>
                    {t('collaboration.pagePdfDescription')}
                  </AttachmentCardMeta>
                </AttachmentCardInfo>
              </AttachmentCard>

              {attachmentsQuery.isLoading && (
                <AttachmentStatusRow>
                  <Loader size={14} className="spin" />
                  <span>{t('collaboration.loadingAttachments')}</span>
                </AttachmentStatusRow>
              )}

              {!attachmentsQuery.isLoading && attachmentError && (
                <AttachmentStatusRow>
                  <RefreshCw size={14} />
                  <span>{attachmentError}</span>
                </AttachmentStatusRow>
              )}

              {!attachmentsQuery.isLoading && !attachmentError && attachments.length === 0 && (
                <AttachmentStatusRow>
                  <span>{t('collaboration.noAttachments')}</span>
                </AttachmentStatusRow>
              )}

              {!attachmentsQuery.isLoading && !attachmentError && attachments.map((att) => {
                const isChecked = focusedPageSelected
                  && isCollaborationAttachmentSelectable(att)
                  && !excludedAttachmentIds.has(att.id);
                const attachmentSizeSelectable = isAttachmentSizeAllowed(att.size);
                const attachmentSelectable = isCollaborationAttachmentSelectable(att);
                const attachmentCountSelectable = isChecked || canApplyFocusedSelectionCount(focusedPageSelectedFileCount + 1);
                const attachmentDisabled = !attachmentSelectable || !attachmentCountSelectable;
                let attachmentDisabledTitle: string | undefined;

                if (!Boolean((att.download_url || '').trim())) {
                  attachmentDisabledTitle = t('collaboration.cannotDownloadAttachment');
                } else if (!attachmentSizeSelectable) {
                  attachmentDisabledTitle = t('collaboration.fileTooLarge');
                } else if (!attachmentCountSelectable) {
                  attachmentDisabledTitle = t('collaboration.maxAttachments', { max: CHAT_ATTACHMENT_MAX_COUNT });
                }

                return (
                  <AttachmentCard
                    key={att.id}
                    $selected={isChecked}
                    $disabled={attachmentDisabled}
                    onClick={(e) => {
                      const target = e.target as HTMLElement;
                      if (target.closest('label, input, button, a')) return;
                      void setFocusedAttachmentSelected(att, !isChecked);
                    }}
                    title={attachmentDisabledTitle}
                  >
                    <CheckboxCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={isChecked}
                        onChange={(checked) => { void setFocusedAttachmentSelected(att, checked); }}
                        size="sm"
                        disabled={attachmentDisabled}
                      />
                    </CheckboxCell>
                    <AttachmentCardIcon><FileIcon size={14} /></AttachmentCardIcon>
                    <AttachmentCardInfo>
                      <AttachmentCardName>{att.name || t('collaboration.unnamed')}</AttachmentCardName>
                      <AttachmentCardMeta>
                        {att.media_type ? `${att.media_type} · ` : ''}
                        {formatFileSize(att.size)}
                      </AttachmentCardMeta>
                    </AttachmentCardInfo>
                    {att.download_url && (
                      <AttachmentCardLink
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleOpenAttachment(att.download_url);
                        }}
                      >
                        {t('collaboration.open')}
                        <ExternalLink size={12} />
                      </AttachmentCardLink>
                    )}
                  </AttachmentCard>
                );
              })}
            </AttachmentCards>

          </DetailContent>
        )}
      </DetailPane>
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('collaboration.title')}
      titleIcon={<Blocks size={18} />}
      width="860px"
      resizable
      defaultHeight={640}
      minWidth={480}
      minHeight={360}
      centerPaddingLeft={modalCenterPaddingLeft}
      centerPaddingRight={modalCenterPaddingRight}
    >
      <ModalBody>
        <Tabs
          items={tabItems}
          activeId={activeTab}
          onChange={(id) => setActiveTab(id as CollaborationProvider)}
        />
        <TabContent>
          {pluginsLoading ? (
            <LoadingWrap>
              <RingSpinner size={24} />
            </LoadingWrap>
          ) : !isConnected(activeTab) ? (
            renderNotConnected()
          ) : (
            <BrowserArea>
              <SplitPane>
                {renderListPane()}
                {renderDetailPane()}
              </SplitPane>
              <ActionBar>
                <SelectedCount>{selectedSummary}</SelectedCount>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={selectedFileCount === 0 || attaching}
                  onClick={() => { void handleAttach(); }}
                >
                  {attaching ? t('collaboration.preparingAttach') : t('collaboration.attach')}
                </Button>
              </ActionBar>
            </BrowserArea>
          )}
        </TabContent>
      </ModalBody>
    </Modal>
  );
};

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  margin: 0 -24px -24px;

  * {
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  *::-webkit-scrollbar {
    width: 0;
    height: 0;
    display: none;
  }
`;

const TabContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
`;

const LoadingWrap = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NotConnectedArea = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CollaborationLogoWrap = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    object-fit: contain;
  }
`;

const TabIcon = styled.img`
  width: 14px;
  height: 14px;
  object-fit: contain;
`;

const BrowserArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const SplitPane = styled.div`
  flex: 1;
  display: flex;
  min-height: 0;
`;

const ListPane = styled.div`
  width: 340px;
  min-width: 300px;
  display: flex;
  flex-direction: column;
  border-right: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const SearchArea = styled.div`
  padding: 10px 12px;
  flex-shrink: 0;
`;

const NotionAccessBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 12px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'};
  border-bottom: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'};
`;

const NotionAccessText = styled.span`
  font-size: 0.7rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const NotionAccessButton = styled.button`
  border: none;
  background: none;
  padding: 2px 8px;
  font-size: 0.7rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.accent};
  cursor: pointer;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
  }
  &:disabled {
    cursor: default;
  }
  .spin {
    animation: notion-access-spin 1s linear infinite;
  }
  @keyframes notion-access-spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const ListScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
`;

const PageRow = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  cursor: pointer;
  transition: background ${({ theme }) => theme.transitions.fast};

  ${({ $active, theme }) =>
    $active &&
    css`
      background: ${theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
    `}

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)'};
  }
`;

const TreeIndent = styled.div<{ $depth: number }>`
  width: ${({ $depth }) => `${Math.max(0, $depth) * 14}px`};
  flex-shrink: 0;
`;

const ExpandToggle = styled.button`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textMuted};
  flex-shrink: 0;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const TreeLeafSpacer = styled.span`
  width: 14px;
  height: 14px;
  display: inline-block;
`;

const TreeIconCell = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textMuted};
  flex-shrink: 0;
`;

const CheckboxCell = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
`;

const PageInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const PageTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const PageTitle = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PageLinkButton = styled.button`
  width: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textMuted};
  border: 0;
  padding: 0;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    color: ${({ theme }) => theme.colors.accent};
  }
`;

const PageMeta = styled.div`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TreeStatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px 8px 42px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textMuted};

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const TreeLoadMoreButton = styled.button`
  margin: 4px 0 8px 42px;
  padding: 5px 8px;
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 12px;
  cursor: pointer;
  background: ${({ theme }) => theme.colors.bgSecondary};

  &:hover {
    color: ${({ theme }) => theme.colors.textPrimary};
    background: ${({ theme }) => theme.colors.bgHover};
  }
`;

const DetailPane = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const DetailEmpty = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.875rem;
`;

const DetailLoading = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DetailErrorWrap = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DetailContent = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 14px;
`;

const DetailTitle = styled.h3`
  margin: 0;
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-weight: 600;
`;

const DetailMeta = styled.div`
  margin-top: 6px;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const PageSelectRow = styled.div<{ $active: boolean }>`
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  cursor: pointer;
  transition: background ${({ theme }) => theme.transitions.fast};

  ${({ $active, theme }) =>
    $active &&
    css`
      background: ${theme.mode === 'dark' ? 'rgba(96,165,250,0.08)' : 'rgba(59,130,246,0.05)'};
      border-color: ${theme.colors.accent};
    `}

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)'};
  }
`;

const PageSelectInfo = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const PageSelectTitle = styled.div`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const PageSelectMeta = styled.div`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const AttachmentCards = styled.div`
  margin-top: 10px;
  border-radius: ${({ theme }) => theme.sizes.borderRadius};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  overflow: hidden;
`;

const AttachmentCardsTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const AttachmentQuickActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const AttachmentQuickButton = styled.button<{ $active?: boolean }>`
  height: 24px;
  padding: 0 8px;
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  border: 1px solid ${({ theme, $active }) =>
    $active ? theme.colors.accent : theme.colors.borderLight};
  background: ${({ theme, $active }) =>
    $active
      ? (theme.mode === 'dark' ? 'rgba(96,165,250,0.16)' : 'rgba(59,130,246,0.1)')
      : theme.colors.bgSecondary};
  color: ${({ theme, $active }) =>
    $active ? theme.colors.accent : theme.colors.textSecondary};
  font-size: 0.6875rem;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.textPrimary};
    border-color: ${({ theme }) => theme.colors.accent};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const AttachmentCard = styled.div<{ $selected: boolean; $disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.65 : 1)};
  transition: background ${({ theme }) => theme.transitions.fast};

  ${({ $selected, theme }) =>
    $selected &&
    css`
      background: ${theme.mode === 'dark' ? 'rgba(96,165,250,0.08)' : 'rgba(59,130,246,0.05)'};
    `}

  &:hover {
    background: ${({ $disabled, theme }) =>
      $disabled
        ? 'transparent'
        : (theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)')};
  }

  & + & {
    border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
  }
`;

const AttachmentCardIcon = styled.div`
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const AttachmentCardInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const AttachmentCardName = styled.div`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AttachmentCardMeta = styled.div`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AttachmentCardLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  padding: 0;
  border: 0;
  background: transparent;
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.accent};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const AttachmentStatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textMuted};
  border-top: 1px solid ${({ theme }) => theme.colors.borderLight};

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;


const ActionBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
  flex-shrink: 0;
`;

const SelectedCount = styled.span`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const SkeletonArea = styled.div`
  display: flex;
  flex-direction: column;
  padding: 6px 8px;
`;

const SkeletonRow = styled.div`
  display: grid;
  grid-template-columns: 16px 1fr;
  gap: 8px;
  align-items: center;
  padding: 10px 6px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const LoadMoreIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 0;
  color: ${({ theme }) => theme.colors?.textSecondary ?? '#888'};
  font-size: 13px;

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

